import '../styles/Home.css';
import '../styles/animations.css';
import BotonPlus from '../components/BotonPlus';
import WheelLoader from '../components/WheelLoader';
import HeroSection from '../components/HeroSection';
import SkeletonCard from '../components/SkeletonCard';
import useStaggeredCards from '../hooks/useStaggeredCards';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import { useState, useEffect, useRef } from 'react';

const Home = () => {
  const { user } = useAuth();
  const { searchTerm } = useSearch();
  const [publicaciones, setPublicaciones] = useState([]);
  const [publicacionesFiltradas, setPublicacionesFiltradas] = useState([]); 
  const [skip, setSkip] = useState(0);
  const limit = 8;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageReady, setPageReady] = useState(false);
  const isFirstFetch = useRef(true);
  const { registerCard, getCardStyle } = useStaggeredCards();

  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/^http:/, 'https:');

  const fetchPublicaciones = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/v1/publicacion/?skip=${skip}&limit=${limit}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      
      if (!data.publicaciones) throw new Error('Datos inválidos recibidos del backend');

      setPublicaciones(prev => [
        ...prev,
        ...data.publicaciones.filter(pub => !prev.some(p => p.id === pub.id)),
      ]);
      setTotal(data.total || 0);

      if (isFirstFetch.current) {
        isFirstFetch.current = false;
        const slides = data.publicaciones.filter(p => p.url_portada).slice(0, 5);
        if (slides.length === 0) {
          setPageReady(true);
        } else {
          let loaded = 0;
          const done = () => { if (++loaded >= slides.length) setPageReady(true); };
          const fallback = setTimeout(() => setPageReady(true), 5000);
          slides.forEach(({ url_portada }) => {
            const img = new Image();
            img.onload = () => { done(); if (loaded >= slides.length) clearTimeout(fallback); };
            img.onerror = done;
            img.src = url_portada;
          });
        }
      }
    } catch (err) {
      console.error('Error al traer publicaciones:', err);
      setError('No se pudo cargar las publicaciones.');
      if (isFirstFetch.current) { isFirstFetch.current = false; setPageReady(true); }
    } finally {
      setLoading(false);
    }
  };

  // Filtrar publicaciones basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = publicaciones.filter(pub => 
        pub.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pub.descripcion_corta.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setPublicacionesFiltradas(filtered);
    } else {
      setPublicacionesFiltradas(publicaciones);
    }
  }, [searchTerm, publicaciones]);

  // Cargar publicaciones solo cuando skip cambie Y no haya búsqueda activa
  useEffect(() => {
    if (!searchTerm.trim()) {
      fetchPublicaciones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  // Cargar publicaciones iniciales al montar el componente
  useEffect(() => {
    fetchPublicaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerMas = () => {
    setSkip(prev => prev + limit);
  };

  if (!pageReady) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <WheelLoader />
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
      <HeroSection searchTerm={searchTerm} totalPublicaciones={total} publicacionesFiltradas={publicacionesFiltradas} />
    <div className="main-content">

      {/* Grid de publicaciones */}
      <section className="cars-grid">
        {publicacionesFiltradas.map((pub, index) => (
          <div
            key={pub.id}
            className="car-card"
            ref={(el) => registerCard(el, index)}
            style={getCardStyle(index)}
          >
            {/* Imagen clickeable */}
            <a href={`/post/${pub.id}`} style={{ display: 'block', position: 'relative' }}>
              {index === 0 && !searchTerm.trim() && (
                <span className="car-badge-latest">ÚLTIMO</span>
              )}
              <img
                className="car-image"
                src={pub.url_portada || '/cars/default.jpg'}
                alt={pub.titulo}
              />
            </a>
            <div className="car-content">
              <h2 className="car-title">{pub.titulo}</h2>
              <p className="car-description">{pub.descripcion_corta}</p>
              <div className="car-meta">
                <span className="car-date">
                  Publicado:{' '}
                  {new Date(pub.fecha_publicacion).toLocaleDateString()}
                </span>
                <a className="read-more" href={`/post/${pub.id}`}>
                  Leer más
                </a>
              </div>
            </div>
          </div>
        ))}

        {searchTerm.trim() && publicacionesFiltradas.length === 0 && !loading && (
          <div style={{
            textAlign: 'center',
            gridColumn: '1 / -1',
            padding: '2rem',
            color: '#666'
          }}>
            <p>No se encontraron publicaciones que coincidan con "{searchTerm}"</p>
          </div>
        )}
      </section>

      {/* Estado de carga o error */}
      {loading && (
        <div className="cars-grid" style={{ marginTop: '0' }}>
          {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}
      {error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}

      {/* Botón "Ver más" - solo mostrar si no hay búsqueda activa */}
      {!loading && !searchTerm.trim() && skip + limit < total && (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <button onClick={handleVerMas} className="read-more">
            Ver más
          </button>
        </div>
      )}

      {/* Botón flotante para crear publicación */}
      {user && parseInt(user.tipo_usuario) === 0 && (
        <div style={{ position: 'fixed', bottom: '2rem', left: '2rem', zIndex: 1000 }}>
          <a href="/crearpost">
            <BotonPlus />
          </a>
        </div>
      )}
    </div>
    </div>
  );
};

export default Home;
